import React, { useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Stack,
  Typography,
  Button,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  Alert,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { tokens } from "@/tokens";
import { useAppSelector } from "@/store";
import { roles } from "@/roles";
import {
  useGetFirstSaleSupplierQuery,
  useGetFirstSaleCasesQuery,
  useDeleteFirstSaleSupplierMutation,
} from "@/services/first-sale";
import FirstSaleBreadcrumb from "../components/first-sale-breadcrumb";
import StatusBadge from "../components/status-badge";
import { CasesTable } from "../components/cases-table";
import { CreateCaseModal } from "./create-case-modal";
import { EditSupplierModal } from "../components/edit-supplier-modal";

const SupplierDetail: React.FC = () => {
  const { supplierId } = useParams<{ supplierId: string }>();
  const navigate = useNavigate();
  const [createCaseOpen, setCreateCaseOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [editSupplierOpen, setEditSupplierOpen] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const { user } = useAppSelector((state) => state.auth);
  const { selectedClient } = useAppSelector((state) => state.client);
  const { selectedTenant } = useAppSelector((state) => state.tenantManagement);

  const isTDAdmin = user?.userRole === roles.TDADMIN;
  const isTDAdminTCD = isTDAdmin && selectedTenant?.tenantType === "TRADEDIVE_CLIENT";

  const clientId = useMemo(() => {
    if (isTDAdminTCD) return selectedTenant?._id;
    if (selectedClient?._id) return selectedClient._id;
    if (user?.userRole === roles.TDCLIENT || user?.userRole === roles.SPCLIENT) return user?._id;
    return undefined;
  }, [isTDAdminTCD, selectedTenant, selectedClient, user]);

  const tenantVars = useMemo(() => ({
    ...(clientId && { clientId }),
    ...(isTDAdmin && selectedTenant && { tenantId: selectedTenant._id }),
  }), [clientId, isTDAdmin, selectedTenant]);

  const { data: supplier, isLoading: supplierLoading } = useGetFirstSaleSupplierQuery(
    { supplierId: supplierId!, ...tenantVars },
    { skip: !supplierId }
  );

  const { data: cases = [], isLoading: casesLoading } = useGetFirstSaleCasesQuery(
    { supplierId: supplierId!, ...tenantVars },
    { skip: !supplierId }
  );

  const [deleteSupplier, { isLoading: isDeleting }] = useDeleteFirstSaleSupplierMutation();

  const handleDeleteSupplier = async () => {
    if (!supplierId) return;
    try {
      await deleteSupplier({ supplierId, ...(tenantVars.tenantId ? { tenantId: tenantVars.tenantId } : {}) }).unwrap();
      setDeleteConfirmOpen(false);
      navigate("/first-sale");
    } catch (err: any) {
      setDeleteConfirmOpen(false);
      const message = err?.data?.message || err?.message || "Failed to delete supplier. The supplier may still have cases.";
      setDeleteError(message);
    }
  };

  if (supplierLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <Skeleton width={300} height={24} />
        <Skeleton width={200} height={40} sx={{ mt: 2 }} />
      </Box>
    );
  }

  if (!supplier) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography sx={{ color: tokens.text.secondary }}>Supplier not found.</Typography>
      </Box>
    );
  }

  return (
    <Stack sx={{ height: "100%", overflow: "auto" }}>
      <Box sx={{ p: 3 }}>
        {/* Breadcrumb */}
        <FirstSaleBreadcrumb
          items={[{ label: supplier.supplierName }]}
        />

        {/* Header */}
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 3 }}>
          <Box>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 0.5 }}>
              <Typography
                sx={{
                  fontSize: "1.1rem",
                  fontWeight: tokens.fontWeight.heavy,
                  color: tokens.text.primary,
                }}
              >
                {supplier.supplierName}
              </Typography>
              <StatusBadge status={supplier.status || "ACTIVE"} />
            </Stack>

            {supplier.factoryName && (
              <Typography
                sx={{
                  fontSize: tokens.fontSize.sm2,
                  color: tokens.text.secondary,
                  mb: 0.5,
                }}
              >
                Factory: {supplier.factoryName}
                {supplier.factoryCountry ? ` (${supplier.factoryCountry})` : ""}
              </Typography>
            )}

            <Typography
              sx={{
                fontSize: tokens.fontSize.sm2,
                fontFamily: "monospace",
                color: tokens.text.muted,
                mb: 1.5,
              }}
            >
              MID: {supplier.mid}
            </Typography>

            {/* Metrics row */}
            <Stack direction="row" spacing={4}>
              {[
                { label: "Active Cases", value: (supplier.activeCases || supplier.casesCount || cases.length).toString() },
              ].map((s) => (
                <Box key={s.label}>
                  <Typography
                    sx={{
                      fontSize: tokens.fontSize["2xs"],
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      color: tokens.text.muted,
                      lineHeight: "14px",
                    }}
                  >
                    {s.label}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "1rem",
                      fontWeight: tokens.fontWeight.bold,
                      color: tokens.text.primary,
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {s.value}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Box>

          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              size="small"
              startIcon={<EditIcon sx={{ fontSize: 14 }} />}
              onClick={() => setEditSupplierOpen(true)}
              sx={{
                textTransform: "none",
                fontSize: tokens.fontSize.xs,
                fontWeight: tokens.fontWeight.semibold,
                color: tokens.text.primary,
                border: `1px solid ${tokens.border.default}`,
                borderRadius: tokens.radius.sm,
                px: 1.5,
                py: 0.5,
                "&:hover": { bgcolor: tokens.bg.hover },
              }}
            >
              Edit
            </Button>
            <Button
              size="small"
              startIcon={<DeleteIcon sx={{ fontSize: 14 }} />}
              onClick={() => setDeleteConfirmOpen(true)}
              sx={{
                textTransform: "none",
                fontSize: tokens.fontSize.xs,
                fontWeight: tokens.fontWeight.semibold,
                color: tokens.status.danger,
                border: `1px solid ${tokens.status.danger}`,
                borderRadius: tokens.radius.sm,
                px: 1.5,
                py: 0.5,
                "&:hover": { bgcolor: tokens.status.dangerBg },
              }}
            >
              Delete
            </Button>
            <Button
              startIcon={<AddIcon />}
              variant="contained"
              onClick={() => setCreateCaseOpen(true)}
              sx={{
                textTransform: "none",
                fontSize: tokens.fontSize.sm2,
                fontWeight: tokens.fontWeight.semibold,
                bgcolor: tokens.brand.primary,
                borderRadius: tokens.radius.md,
                px: 2.5,
                boxShadow: "none",
                whiteSpace: "nowrap",
                "&:hover": {
                  bgcolor: tokens.brand.primaryHover,
                  boxShadow: "none",
                },
              }}
            >
              New Case
            </Button>
          </Stack>
        </Stack>

        {/* Cases Table */}
        <CasesTable
          cases={cases}
          isLoading={casesLoading}
          onRowClick={(fsCase: any) => navigate(`/first-sale/case/${fsCase._id}`)}
        />

        {/* Create Case Modal */}
        <CreateCaseModal
          open={createCaseOpen}
          onClose={() => setCreateCaseOpen(false)}
          supplierId={supplierId!}
        />

        {/* Edit Supplier Modal */}
        <EditSupplierModal
          open={editSupplierOpen}
          onClose={() => setEditSupplierOpen(false)}
          supplier={supplier}
          tenantId={tenantVars.tenantId}
        />

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
          <DialogTitle>Delete Supplier</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete supplier {supplier.supplierName}? This supplier must
              have no cases.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteConfirmOpen(false)} disabled={isDeleting}>
              Cancel
            </Button>
            <Button
              onClick={handleDeleteSupplier}
              disabled={isDeleting}
              sx={{
                color: tokens.status.danger,
                "&:hover": { bgcolor: tokens.status.dangerBg },
              }}
            >
              {isDeleting ? "Deleting..." : "Delete"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Error Snackbar */}
        <Snackbar
          open={!!deleteError}
          autoHideDuration={6000}
          onClose={() => setDeleteError(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert onClose={() => setDeleteError(null)} severity="error" variant="filled">
            {deleteError}
          </Alert>
        </Snackbar>
      </Box>
    </Stack>
  );
};

export default SupplierDetail;
